import { FC, useEffect, useState } from "react"
import { LoadingStatus } from "../../../@types/enumsGlobal"
import KizExchangeCommandPanel from "../../../components/commandsPanels/KizExchangeCommandPanel"
import { IDatePeriod } from '../../../components/datePeriodPicker/datePeriodPicker'
import { DefaultGrid } from "../../../components/grids/default/defaultGrid"
import { IOption } from "../../../components/selects/select"
import { DatePeriodPanel } from '../../../hoc/DatePeriodPanel/DatePeriodPanel'
import { IKizExchangeViewDTO } from "../../../libs/coreapi-dto/accounting/KizExchange"
import { KizExchangeDataProvider } from "../../../Services/DataProviders/KizExchangeDataProvider"
import useGridFilter from "../../../system/hooks/useGridFilter"
import { useAppContext } from "../../../system/providers/appContextProvider"
import { KizParsed } from "../../Dictionaries/Kiz/KizParsed"
import styles from "../../styles/index.module.scss"
import KizExchangeGridPluginSettings from "./KizExchangeGridPluginSettings"
import { DateTime } from "luxon";

interface IKizExchangeGridByLotProps {
    idGlobal: string;
    goodsCode: string;
    barcode?: string;
}

interface IKizExchangeColumnFilter {
    sscc: string | null;
    gtinSGtin: string | null;
    barcode: string | null;
    internalBarcode: string | null;
    goodsName: string | null;
    documentNumber: string | null;
    code: string | null;
    decodedBarcode: string | null;
}

interface IKizParamFilter {
    typeSearchField: TypeSearchField;
    autofilter: boolean;
    dynamicSearch: boolean;
}

type TypeSearchField = "documentNumber" | "internalBarcode" | "sscc" | "gtinSGtin" | "barcode" | "goodsName" | "internalBarcode" | "code" | "decodedBarcode";

const createColumnFilter = (filter: IKizExchangeColumnFilter, withinRange: boolean) => {
    let columnFilter = filter;
    let newColumnFilters: IGridColumnFilter[] = [] as IGridColumnFilter[];
    for (let key in getFilter(columnFilter)) {
        let value = getFilter(columnFilter)[key];
        value = key === "barcode" ? KizParsed.encodeBarcode(value) : value;
        if (value !== null && value !== "") {
            let additional = withinRange ? "%" : "";
            newColumnFilters.push({
                name: key,
                operator: withinRange ? "Like" : "Eq",
                value: value + additional,
            });
        }
    }
    return newColumnFilters;
};

export const KizExchangeGridByLot: FC<IKizExchangeGridByLotProps> = (props) => {
    const appCtx = useAppContext();
    const kizExchangeDataProvider = new KizExchangeDataProvider(appCtx.coreApiService);

    const minDateText = '19000101';
    const minDate = DateTime.fromFormat(minDateText, 'yyyyMMdd');

    const DefaultDatePeriod = {
        startDate: minDate,
        endDate: DateTime.now(),
    } as IDatePeriod;
    
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [data, setData] = useState<IKizExchangeViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [searchInput, setSearchInput] = useState<string | undefined>(props?.barcode ?? "");
    const [filterBy, setFilterBy] = useState<IOption | undefined>();
    const [autoFilter, setAutoFilter] = useState<boolean>(false);
    const [kizParamFilter, setKizParamFilter] = useState<IKizParamFilter>({
        autofilter: false,
        typeSearchField: "barcode",
    } as IKizParamFilter);
    const [withinRange, setWithinRange] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>();
    const [kizExchangeColumnFilter, setKizExchangeColumnFilter] = useState<IKizExchangeColumnFilter>({
        sscc: null,
        gtinSGtin: null,
        barcode: null,
        internalBarcode: null,
        goodsName: null,
        documentNumber: null,
        code: null,
        decodedBarcode: null,
    } as IKizExchangeColumnFilter);

    const DefaultGridFilter = (datePeriod) => {
        return {
            numberPerPage: 15,
            pageNumber: 1,
            columnFilters: [
                ...(datePeriod?.startDate
                    ? [
                          {
                              name: "dateOP",
                              value: datePeriod.startDate.toFormat("yyyyMMdd"),
                              operator: "MoreOrEq",
                              invisible: true,
                          } as IGridColumnFilter,
                      ]
                    : []),
                ...(datePeriod?.endDate
                    ? [
                          {
                              name: "dateOP",
                              value: datePeriod.endDate.plus({ days: 1 }).toFormat("yyyyMMdd"),
                              operator: "LessOrEq",
                              invisible: true,
                          } as IGridColumnFilter,
                      ]
                    : []),
            ],
        };
    };

    function setDefaultGridFilter() {
            let baseFilter;
    
            if (props.barcode) {
                const filter = {
                    sscc: null,
                    gtinSGtin: null,
                    barcode: props.barcode,
                    internalBarcode: null,
                    goodsName: null,
                    documentNumber: null,
                    code: null,
                    decodedBarcode: null,
                } as IKizExchangeColumnFilter;
    
                let createColomns = createColumnFilter(filter, false);
                baseFilter = {
                    numberPerPage: 150,
                    pageNumber: 1,
                    columnFilters: createColomns,
                };
            } else {
                baseFilter = {
                    numberPerPage: 150,
                    pageNumber: 1,
                    columnFilters: [],
                };
            }
    
            if (props.idGlobal) {
                baseFilter.columnFilters.push({
                    name: "IdLotGlobal",
                    value: props.idGlobal,
                    operator: "Eq",
                } as IGridColumnFilter);
            }
            // Всегда добавляем период дат
            const dateFilter = DefaultGridFilter(DefaultDatePeriod);
            baseFilter.columnFilters = [...baseFilter.columnFilters, ...dateFilter.columnFilters];
    
            return baseFilter;
        }

    

    useEffect(() => {
        if (props.barcode) {
            const filter = { ...kizExchangeColumnFilter, barcode: props.barcode };
            setKizExchangeColumnFilter(filter);
            setSearchInput(props.barcode);
        }
    }, [props.barcode]);

    function refreshGridFilter(columnFilter: IKizExchangeColumnFilter, withinRange: boolean, datePeriod?: IDatePeriod) {
        let createColomns = createColumnFilter(columnFilter, withinRange);
        let createColomnsExtra = [...createColomns];

        if (props.idGlobal) {
            const idLotGlobalFilter = createColomnsExtra.find((f) => f.name === "IdLotGlobal");
            if (!idLotGlobalFilter) {
                createColomnsExtra.push({
                    name: "IdLotGlobal",
                    value: props.idGlobal,
                    operator: "Eq",
                } as IGridColumnFilter);
            }
        }
        if (datePeriod) {            
            if (datePeriod.startDate) {
                createColomnsExtra.push({
                    name: "dateOP",
                    value: datePeriod.startDate.toFormat("yyyyMMdd"),
                    operator: "MoreOrEq",
                } as IGridColumnFilter);
                if (datePeriod.endDate) {
                    createColomnsExtra.push({
                        name: "dateOP",
                        value: datePeriod.endDate.plus({ days: 1 }).toFormat("yyyyMMdd"),
                        operator: "LessOrEq",
                    } as IGridColumnFilter);
                }
            }
        }

        dispatchGridFilter({
            type: "sortByFilters",
            payload: {
                gridColumnFilter: createColomnsExtra,
            },
        });
    }
    const [gridFilter, dispatchGridFilter] = useGridFilter(setDefaultGridFilter());

    useEffect(() => {
        kizExchangeDataProvider.getView(gridFilter, (data, totalCount) => {
            setData(data);
            setTotalCount(totalCount);
            data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
        });
    }, [gridFilter]);

    return (
        <div className={styles.gridWrapper}>
           
            <KizExchangeCommandPanel
                searchInput={[searchInput, setSearchInput]}
                kizExchangeColumnFilter={[
                    kizExchangeColumnFilter,
                    (value, update) => {
                        setKizExchangeColumnFilter(value);
                        if (update) refreshGridFilter(value, withinRange);
                    },
                ]}
                filterBy={[filterBy, setFilterBy]}
                autoFilter={[autoFilter, setAutoFilter]}
                kizParamFilter={[kizParamFilter, (value: IKizParamFilter) => setKizParamFilter(value)]}
                withinRange={[withinRange, setWithinRange]}
                dispatchGridFilter={(value) => dispatchGridFilter(value)}
                onWithinRange={(value) => {
                    refreshGridFilter(kizExchangeColumnFilter, value);
                }}
                search={{
                    onClick: () => {
                        refreshGridFilter(kizExchangeColumnFilter, withinRange);
                    },
                }}
                clearFilters={{
                    onClick: () => {
                        setSearchInput("");
                        const filterEmpty = {
                            sscc: null,
                            gtinSGtin: null,
                            barcode: null,
                            internalBarcode: null,
                            goodsName: null,
                            documentNumber: null,
                            code: null,
                            decodedBarcode: null,
                        } as IKizExchangeColumnFilter;
                        setKizExchangeColumnFilter(filterEmpty);
                        refreshGridFilter(filterEmpty, withinRange);
                    },
                }}
            />
            <DatePeriodPanel
                            datePeriod={datePeriod}
                            onDatePeriodChange={(date: IDatePeriod) => {
                                setDatePeriod(date);
                                refreshGridFilter(kizExchangeColumnFilter, withinRange, date);
                            }}
                        />

            <DefaultGrid
                separator={true}
                gridId={`kiz-exchange-tab-${props.idGlobal}`}
                data={data}
                loadingStatus={loadingStatus}
                totalCount={totalCount}
                filter={gridFilter}
                dataProvider={kizExchangeDataProvider}
                plugin={KizExchangeGridPluginSettings}
                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                kizStatus={true}
                onSort={(sortInfo) => dispatchGridFilter({ type: "sort", payload: sortInfo.propertyName })}
                onFilterDelete={(filterInfo) => dispatchGridFilter({ type: "deleteColumnFilter", payload: filterInfo.propertyName })}
                selectedItem={selectedItem}
                onSelect={(row) => setSelectedItem(row)}
                onEnter={() => {}}
                setLoadingStatus={setLoadingStatus}
            />
        </div>
    );
};

const getFilter = (filter: IKizExchangeColumnFilter) => {
    let newFilter = new Object();
    for (let key in filter) {
        if (filter[key] !== null) {
            newFilter[key] = filter[key];
        }
    }
    return newFilter;
};
export default KizExchangeGridByLot;
